import { Code, Server, Database, Cpu, Radio, Cloud, Server as ServerIcon, Wrench, BookOpen, GraduationCap, FileText, Package, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import TechStack from '@/components/TechStack';
import ScrollReveal, { StaggerGroup } from '@/components/ScrollReveal';
import TestimonialCarousel from '@/components/TestimonialCarousel';
import StarBorderButton from '@/components/StarBorderButton';
import SpotlightCard from '@/components/SpotlightCard';

export default function Services() {
  const webDevServices = [
    {
      icon: <Code size={28} />,
      title: 'Frontend Development',
      technologies: ['React', 'Vue.js', 'Next.js', 'Tailwind CSS', 'Responsive Design'],
      description: 'UI/UX implementation yang modern, responsive, dan user-friendly dengan performa optimal.'
    },
    {
      icon: <Server size={28} />,
      title: 'Backend Development',
      technologies: ['Node.js', 'Express', 'FastAPI', 'REST API', 'GraphQL'],
      description: 'Backend architecture yang scalable, secure, dan mudah di-maintain dengan dokumentasi lengkap.'
    },
    {
      icon: <Database size={28} />,
      title: 'Database & Integration',
      technologies: ['PostgreSQL', 'MongoDB', 'Redis', 'API Integration'],
      description: 'Database design, optimization, dan integrasi dengan third-party services.'
    },
    {
      icon: <Cloud size={28} />,
      title: 'DevOps & Deployment',
      technologies: ['Docker', 'AWS', 'DigitalOcean', 'Nginx', 'PM2', 'CI/CD'],
      description: 'Setup infrastructure, deployment automation, dan monitoring untuk production-ready apps.'
    }
  ];

  const iotServices = [
    {
      title: 'ESP32/ESP8266 Development',
      points: [
        'Firmware development dengan Arduino/PlatformIO',
        'Sensor integration (DHT, BME, GPS, dsb)',
        'Actuator control (relay, motor, LED)',
        'Power management & optimization'
      ]
    },
    {
      title: 'IoT Communication & Integration',
      points: [
        'MQTT broker setup & implementation',
        'WebSocket real-time communication',
        'REST API untuk IoT devices',
        'Cloud integration (AWS IoT, Google IoT)'
      ]
    },
    {
      title: 'Dashboard & Monitoring',
      points: [
        'Real-time data visualization',
        'Device management interface',
        'Historical data analytics',
        'Alert & notification system'
      ]
    },
    {
      title: 'PCB Design & Prototyping',
      points: [
        'Schematic design',
        'PCB layout',
        'Component selection',
        'Prototype testing & validation',
        'Production file preparation'
      ]
    }
  ];

  const engineeringSupport = [
    {
      icon: <Cpu size={24} />,
      title: 'IoT Project Mentoring',
      description: 'Bimbingan konsep, arsitektur sistem, review kode, dan debugging untuk project IoT yang terintegrasi dengan web.',
      topics: ['System architecture', 'Hardware selection', 'Code review', 'Troubleshooting']
    },
    {
      icon: <GraduationCap size={24} />,
      title: 'MATLAB Guidance',
      description: 'Pendampingan penggunaan MATLAB untuk simulasi, analisis data, dan visualisasi hasil.',
      topics: ['Signal processing', 'Control systems', 'Data analysis', 'Plotting & visualization']
    },
    {
      icon: <FileText size={24} />,
      title: 'CAD Software Training',
      description: 'Bimbingan AutoCAD dan Fusion 360 untuk desain mekanik dan dokumentasi teknik.',
      topics: ['2D drafting', '3D modeling', 'Assembly design', 'Technical drawing']
    },
    {
      icon: <Radio size={24} />,
      title: 'PCB Design Support',
      description: 'Pendampingan desain skematik, layout PCB, hingga persiapan file untuk manufaktur.',
      topics: ['Schematic design', 'PCB layout', 'Component selection']
    }
  ];

  const packages = [
    {
      name: 'Landing Page',
      description: 'Website satu halaman untuk personal brand atau produk',
      features: ['Responsive design', 'Contact form', 'SEO optimization', 'Fast loading'],
      ideal: 'Personal brand, product launch, portfolio sederhana'
    },
    {
      name: 'Company Profile',
      description: 'Website multi-halaman untuk bisnis atau organisasi',
      features: ['Multiple pages', 'Admin panel', 'Blog system', 'Email integration'],
      ideal: 'UMKM, startup, organisasi kampus'
    },
    {
      name: 'Dashboard Application',
      description: 'Aplikasi web dengan backend dan database',
      features: ['User authentication', 'Database integration', 'API endpoints', 'Real-time updates'],
      ideal: 'Internal tools, monitoring system, data management'
    },
    {
      name: 'IoT System',
      description: 'Hardware + software solution lengkap',
      features: ['ESP32/ESP8266 firmware', 'Web dashboard', 'Real-time monitoring', 'Cloud deployment'],
      ideal: 'Research project, PoC, automation system'
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero */}
      <section className="pt-32 pb-20 px-6 noise-overlay">
        <div className="container mx-auto text-center">
          <div className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium mb-4">
            Layanan Lengkap
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="services-title">
            Layanan Engineering
            <br />
            <span className="gradient-text">End-to-End</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            Dari konsep hingga deployment. Kami menyediakan solusi lengkap untuk kebutuhan proyek Anda.
          </p>
        </div>
      </section>

      {/* Web Development */}
      <section className="py-20 px-6 bg-slate-900/50" data-testid="web-dev-section">
        <div className="container mx-auto">
          <ScrollReveal animation="fade-up" className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Web Development End-to-End
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Full-stack development dengan teknologi modern dan best practices
            </p>
          </ScrollReveal>

          <StaggerGroup 
            staggerDelay={0.15}
            animation="fade-up"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          >
            {webDevServices.map((service, index) => (
              <SpotlightCard key={index} className="glass-effect p-6 border-slate-700 card-hover group hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300" data-testid={`web-service-${index}`}>
                <div className="text-cyan-400 mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 transition-colors duration-300 group-hover:text-cyan-400" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {service.title}
                </h3>
                <p className="text-slate-400 text-sm mb-4">{service.description}</p>
                <div className="flex flex-wrap gap-2">
                  {service.technologies.map((tech, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded hover:bg-cyan-500/20 transition-colors duration-200 cursor-default">
                      {tech}
                    </span>
                  ))}
                </div>
              </SpotlightCard>
            ))}
          </StaggerGroup>

          {/* Package Options */}
          <div className="mt-16">
            <ScrollReveal animation="fade-up">
              <h3 className="text-2xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Pilihan Paket Project
              </h3>
            </ScrollReveal>
            <StaggerGroup 
              staggerDelay={0.2}
              animation="scale"
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {packages.map((pkg, index) => (
                <SpotlightCard key={index} className="glass-effect p-8 border-slate-700 group hover:border-cyan-500/50 hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300" data-testid={`package-${index}`}>
                  <h4 className="text-xl font-bold text-white mb-2 transition-colors duration-300 group-hover:text-cyan-400" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {pkg.name}
                  </h4>
                  <p className="text-slate-400 text-sm mb-4">{pkg.description}</p>
                  <div className="space-y-2 mb-4">
                    {pkg.features.map((feature, i) => (
                      <div key={i} className="flex items-center space-x-2 group/feature">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full transition-transform duration-200 group-hover/feature:scale-150"></div>
                        <span className="text-slate-300 text-sm transition-colors duration-200 group-hover/feature:text-white">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-4 border-t border-slate-700 transition-colors duration-300 group-hover:border-cyan-500/30">
                    <p className="text-xs text-slate-500 mb-1">Cocok untuk:</p>
                    <p className="text-sm text-slate-300 transition-colors duration-300 group-hover:text-white">{pkg.ideal}</p>
                  </div>
                </SpotlightCard>
              ))}
            </StaggerGroup>
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <TechStack />

      {/* IoT Development */}
      <section className="py-20 px-6" data-testid="iot-section">
        <div className="container mx-auto">
          <ScrollReveal animation="fade-up" className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              IoT Development & Integration
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Solusi IoT dari hardware programming hingga cloud integration
            </p>
          </ScrollReveal>

          <ScrollReveal animation="fade-up" delay={0.2}>
            <Accordion type="single" collapsible className="space-y-4 max-w-4xl mx-auto">
              {iotServices.map((service, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="glass-effect border-slate-700 rounded-lg px-6 hover:border-cyan-500/50 transition-all duration-300" data-testid={`iot-accordion-${index}`}>
                  <AccordionTrigger className="text-white font-semibold hover:text-cyan-400 transition-colors duration-200" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {service.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2 pt-2">
                      {service.points.map((point, i) => (
                        <li key={i} className="flex items-start space-x-2 group/item">
                          <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-2 flex-shrink-0 transition-transform duration-200 group-hover/item:scale-150"></div>
                          <span className="text-slate-300 text-sm transition-colors duration-200 group-hover/item:text-white">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </ScrollReveal>

          <ScrollReveal animation="scale" delay={0.3} className="mt-12 max-w-4xl mx-auto">
            <Card className="glass-effect p-8 border-slate-700 group hover:border-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-3 transition-colors duration-300 group-hover:text-cyan-400" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Use Cases IoT
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Monitoring suhu & kelembapan ruangan',
                  'Smart home automation',
                  'Asset tracking system',
                  'Environmental monitoring',
                  'Greenhouse automation',
                  'Energy consumption monitoring'
                ].map((useCase, i) => (
                  <div key={i} className="flex items-center space-x-2 group/case">
                    <Cpu size={16} className="text-cyan-400 transition-transform duration-200 group-hover/case:scale-110 group-hover/case:rotate-6" />
                    <span className="text-slate-300 text-sm transition-colors duration-200 group-hover/case:text-white">{useCase}</span>
                  </div>
                ))}
              </div>
            </Card>
          </ScrollReveal>
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="py-20 px-6 bg-slate-900/50" data-testid="troubleshoot-section">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Wrench size={48} className="text-cyan-400 mx-auto mb-4" />
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Troubleshooting & Maintenance
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Ada bug di website atau sistem IoT? Kami bantu identifikasi dan perbaiki
              </p>
            </div>

            <Card className="glass-effect p-8 border-slate-700">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Tipe Masalah yang Ditangani
                  </h3>
                  <ul className="space-y-2">
                    {[
                      'Website error atau tidak bisa diakses',
                      'Server down atau slow response',
                      'Database connection issues',
                      'API integration problems',
                      'IoT device tidak terkoneksi',
                      'Bug di frontend atau backend'
                    ].map((issue, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-2"></div>
                        <span className="text-slate-300 text-sm">{issue}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Workflow Troubleshooting
                  </h3>
                  <div className="space-y-3">
                    {[
                      { step: '1', text: 'Analisa awal & root cause analysis' },
                      { step: '2', text: 'Estimasi waktu & effort perbaikan' },
                      { step: '3', text: 'Implementation & testing' },
                      { step: '4', text: 'Patch deployment & dokumentasi' }
                    ].map((item, i) => (
                      <div key={i} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-cyan-500/20 text-cyan-400 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {item.step}
                        </div>
                        <span className="text-slate-300 text-sm pt-0.5">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Engineering Support */}
      <section className="py-20 px-6" data-testid="engineering-support-section">
        <div className="container mx-auto">
          <ScrollReveal animation="fade-up" className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Engineering & Learning Support
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto mb-6">
              Bimbingan teknis untuk mahasiswa dan profesional
            </p>
            {/* <div className="inline-block px-6 py-3 bg-amber-500/10 border border-amber-500/30 rounded-lg hover:bg-amber-500/20 hover:border-amber-500/50 transition-all duration-300">
              <p className="text-amber-400 text-sm font-medium">
                ⚠️ Layanan ini berupa <strong>bimbingan, konsultasi, dan mentoring</strong> — bukan jasa mengerjakan tugas/skripsi
              </p>
            </div> */}
          </ScrollReveal>

          <StaggerGroup 
            staggerDelay={0.15}
            animation="fade-up"
            className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto"
          >
            {engineeringSupport.map((support, index) => (
              <Card key={index} className="glass-effect p-8 border-slate-700 card-hover group hover:scale-105 hover:border-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/20 transition-all duration-300" data-testid={`support-card-${index}`}>
                <div className="text-cyan-400 mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">{support.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-2 transition-colors duration-300 group-hover:text-cyan-400" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {support.title}
                </h3>
                <p className="text-slate-400 text-sm mb-4">{support.description}</p>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 mb-2">Topics:</p>
                  <div className="flex flex-wrap gap-2">
                    {support.topics.map((topic, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-slate-800 text-slate-300 rounded hover:bg-slate-700 hover:text-white transition-all duration-200 cursor-default">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Digital Products */}
      <section className="py-20 px-6 bg-slate-900/50" data-testid="digital-products-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <Package size={48} className="text-cyan-400 mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Digital Products
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              E-book, course, dan template untuk mempercepat learning journey Anda
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                title: 'E-book: IoT Fundamental',
                description: 'Panduan lengkap memulai project IoT dengan ESP32',
                status: 'Coming Soon',
                statusColor: 'amber'
              },
              {
                title: 'Mini Course: Web Dev Basics',
                description: 'Belajar HTML, CSS, JavaScript dari nol',
                status: 'Coming Soon',
                statusColor: 'amber'
              },
              {
                title: 'Template: IoT Dashboard',
                description: 'Starter template untuk monitoring dashboard',
                status: 'Coming Soon',
                statusColor: 'cyan'
              }
            ].map((product, index) => (
              <Card key={index} className="glass-effect p-6 border-slate-700" data-testid={`digital-product-${index}`}>
                <div className={`inline-block px-3 py-1 bg-${product.statusColor}-500/10 border border-${product.statusColor}-500/30 rounded-full text-${product.statusColor}-400 text-xs font-medium mb-4`}>
                  {product.status}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {product.title}
                </h3>
                <p className="text-slate-400 text-sm">{product.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-900/50 overflow-hidden" data-testid="services-testimonials-section">
        <div className="container mx-auto px-6">
          <ScrollReveal animation="fade-up" className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Hasil yang Berbicara
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Klien kami dari berbagai industri - dari startup hingga UMKM dan mahasiswa
            </p>
          </ScrollReveal>
        </div>

        <ScrollReveal animation="fade-up" delay={0.2}>
          <TestimonialCarousel />
        </ScrollReveal>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="container mx-auto">
          <ScrollReveal animation="scale">
            <Card className="glass-effect border-slate-700 p-6 sm:p-12 text-center hover:border-cyan-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-cyan-500/30">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Butuh Layanan Spesifik?
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto mb-8">
                Diskusikan kebutuhan project Anda. Kami bisa custom solution sesuai requirement.
              </p>
              <div className="flex justify-center">
                <Link to="/contact" className="w-full sm:w-auto">
                  <StarBorderButton className="btn-primary text-base px-8 py-6 group w-full sm:w-auto" data-testid="services-cta">
                    Konsultasi Sekarang
                    <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </StarBorderButton>
                </Link>
              </div>
            </Card>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}